import { useContext, useMemo } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { FormControl } from '@mui/material';
import { CheckboxDropdown } from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtom, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { type ModelsToggles } from '@/modules/annotator/findingSidebar/atoms';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { disableKeyPressAtom, selectedTeethPreviewAtom } from '../../atoms';
import { usePatientPrintoutData } from '../hooks/usePatientPrintoutData';
import { useTeethOptions } from '../hooks/useTeethOptions';

import { PreviewSidebarFindingsFilter } from './PreviewSidebarFindingsFilter';
import { PreviewSidebarNotes } from './PreviewSidebarNotes';
import { PreviewSidebarPatientEducation } from './PreviewSidebarPatientEducation';
import { ThemeModeContext } from './QRPrintDownloadContent';

interface PreviewSidebarConfigurationProps {
  enabledFindingTypes: ModelsToggles;
  setEnabledFindingTypes: React.Dispatch<React.SetStateAction<ModelsToggles>>;
}

export const PreviewSidebarConfiguration = ({
  enabledFindingTypes,
  setEnabledFindingTypes,
}: PreviewSidebarConfigurationProps) => {
  const mode = useContext(ThemeModeContext);
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const { mixPanelData } = usePatientPrintoutData();
  const { t } = useLingui();
  const setDisableKeyPress = useSetAtom(disableKeyPressAtom);
  const [selectedTeeth, setSelectedTeeth] = useAtom(selectedTeethPreviewAtom);
  const teethOptions = useTeethOptions();
  const { trackEvent } = useTrackEvent();

  const findingTypes: { label: string; value: keyof ModelsToggles }[] =
    useMemo(() => {
      return [
        { label: t`Carious Lesion`, value: 'caries' },
        { label: t`Incipient`, value: 'incipients' },
        {
          label: t`Radiographic Bone-level & Calculus`,
          value: 'radiographic_boneloss',
        },
        { label: t`Periapical Radiolucency`, value: 'periapical_radiolucency' },
      ];
    }, [t]);

  return (
    <div className="vai-flex-1 vai-overflow-y-auto vai-p-6">
      {/* Teeth Multi-Select Dropdown */}
      <FormControl fullWidth>
        <CheckboxDropdown
          type="teeth"
          darkMode={mode === 'dark'}
          items={teethOptions}
          getItemLabel={(item) => item.label}
          getItemValue={(item) => item.value}
          onChange={(selectedItems) => {
            trackEvent(mixpanelEvents.previewDialogSelectTooth, {
              teeth: selectedItems.map((item) => item.value),
              ...mixPanelData,
              isClearing: false,
              isIframe: !!isIframe,
              externalPatientId,
              externalImageId,
              patientId: selectedPatientId ?? patientId,
              trialStatus,
              centralizedBilling: isCentralizedBilling,
            });
            setSelectedTeeth(selectedItems.map((item) => item.value));
          }}
          selectedItems={teethOptions.filter((option) =>
            selectedTeeth.includes(option.value),
          )}
          placeholder={t`Select Teeth`}
        />
      </FormControl>

      {/* Clear Teeth Button */}
      {selectedTeeth.length > 0 && (
        <div className="vai-mt-2">
          <button
            data-testid="clear-selected-teeth-button"
            type="button"
            onClick={() => {
              trackEvent(mixpanelEvents.previewDialogSelectTooth, {
                ...mixPanelData,
                isIframe: !!isIframe,
                isClearing: true,
                externalPatientId,
                externalImageId,
                patientId: selectedPatientId ?? patientId,
                teeth: [],
                trialStatus,
                centralizedBilling: isCentralizedBilling,
              });
              setSelectedTeeth([]);
            }}
            className="vai-text-sm vai-text-gray-400 hover:vai-text-gray-200 vai-font-inter vai-underline vai-cursor-pointer vai-bg-transparent vai-border-none vai-p-0"
          >
            {t`Clear Selected Teeth`}
          </button>
        </div>
      )}

      <div className="vai-mt-6">
        <span
          className={classNames('vai-font-semibold vai-font-inter', {
            'vai-text-foreground-dark': mode === 'dark',
            'vai-text-zinc-900': mode === 'light',
          })}
        >
          <Trans>Configurations</Trans>
        </span>

        <PreviewSidebarPatientEducation isEditMode />
        {/* Notes */}
        <div className="vai-mt-6">
          <PreviewSidebarNotes setDisableKeyPress={setDisableKeyPress} />
        </div>
        {/* AI Findings to Include */}
        <div className="vai-mt-6">
          <PreviewSidebarFindingsFilter
            enabledFindingTypes={enabledFindingTypes}
            setEnabledFindingTypes={setEnabledFindingTypes}
            findingTypes={findingTypes}
          />
        </div>
      </div>
    </div>
  );
};
